import {Component, ViewChild} from '@angular/core';
import {JSONSchema7} from 'json-schema';
import {ButtonSeverity} from 'primeng/button';
import {DynamicDialogConfig} from 'primeng/dynamicdialog';
import {IBaseModalComponent} from '../../components/ibase-modal/ibase-modal.component';
import {JsonSchemaFormComponent} from '../../components/json-schema-form/json-schema-form.component';
import {PrimeNgImportsModule} from '../../prime-ng-imports';
import {ConfirmationModalComponent, ConfirmationModalConfig} from '../confirm-modal/confirm-modal.component';

/**
 * Json schema form modal configuration
 */
export class JsonSchemaFormModalConfig {
    constructor(
        public title: string,
        public schema: JSONSchema7,
        public rootSchema?: JSONSchema7,
        public readOnly?: boolean,
        public cancelButtonLabel?: string,
        public cancelButtonIcon?: string,
        public cancelButtonSeverity?: ButtonSeverity,
        public validateButtonLabel?: string,
        public validateButtonIcon?: string,
        public validateButtonSeverity?: ButtonSeverity,
        public data?: unknown,
        public validateConfirmation?: ConfirmationModalConfig,
        public validateAsync?: (data: unknown) => Promise<boolean | void>
    ) {}
}

/**
 * Json schema form modal result
 */
export class JsonSchemaFormModalResult {
    constructor(
        public validated: boolean,
        public data?: unknown
    ) {}
}

/**
 * Json schema form modal component
 */
@Component({
    selector: 'app-json-schema-form-modal',
    templateUrl: './json-schema-form-modal.component.html',
    imports: [PrimeNgImportsModule, JsonSchemaFormComponent]
})
export class JsonSchemaFormModalComponent extends IBaseModalComponent<
    JsonSchemaFormModalComponent,
    JsonSchemaFormModalConfig
> {
    @ViewChild('jsonSchemaForm')
    protected jsonSchemaForm?: JsonSchemaFormComponent;

    protected _isJsonSchemaFormValid = false;
    protected _isSubmitting = false;

    // ===============================================================
    // ===============================================================

    /**
     * Get dialog service parameters
     * @param dialogOpenMode Dialog open mode
     * @param extended Extended config
     * @returns DynamicDialogConfig<JsonSchemaFormModalConfig>
     */
    public static getDialogServiceParameters(
        config: JsonSchemaFormModalConfig
    ): DynamicDialogConfig<JsonSchemaFormModalConfig> {
        if (!config.readOnly) config.readOnly = false;

        if (!config.cancelButtonLabel) config.cancelButtonLabel = 'Cancel';
        if (!config.cancelButtonIcon) config.cancelButtonIcon = 'pi pi-times';
        if (!config.cancelButtonSeverity) config.cancelButtonSeverity = 'contrast';

        if (!config.validateButtonLabel) config.validateButtonLabel = 'Confirm';
        if (!config.validateButtonIcon) config.validateButtonIcon = 'pi pi-check';
        if (!config.validateButtonSeverity) config.validateButtonSeverity = 'primary';

        return {
            modal: true,
            header: config.title,
            data: config,
            resizable: true
        };
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On cancel button
     */
    protected onCancel() {
        this.ref.close(new JsonSchemaFormModalResult(false));
    }

    /**
     * On validate button.
     * Returns the form data as-is; writeOnly secret fields keep whatever value they currently
     * hold (untouched original value if left masked, or the new user-provided value otherwise).
     * Filtering of unchanged secrets is the responsibility of the consuming modal at final submission.
     */
    protected async onValidate() {
        if (!this._isJsonSchemaFormValid || this._isSubmitting) {
            return;
        }

        const formData = this.jsonSchemaForm?.getData();

        const validateAndClose = async () => {
            if (this.config.data?.validateAsync) {
                this._isSubmitting = true;

                try {
                    const canClose = await this.config.data.validateAsync(formData);
                    if (canClose === false) {
                        return;
                    }
                } catch (err: unknown) {
                    this.globalsService.messageService.add({
                        key: this.overrideMessagesKey,
                        severity: 'error',
                        summary: 'Unable to save changes',
                        detail: this.getErrorDetail(err)
                    });
                    return;
                } finally {
                    this._isSubmitting = false;
                }
            }

            this.ref.close(new JsonSchemaFormModalResult(true, formData));
        };

        const validateConfirmation = this.config.data?.validateConfirmation;
        if (!validateConfirmation) {
            await validateAndClose();
            return;
        }

        this.globalsService.dialogService
            .open(
                ConfirmationModalComponent,
                ConfirmationModalComponent.getDialogServiceParameters(validateConfirmation)
            )
            .onClose.subscribe((confirmed: boolean) => {
                if (confirmed) {
                    void validateAndClose();
                }
            });
    }

    private readonly getErrorDetail = (err: unknown): string => {
        if (err && typeof err === 'object') {
            const maybeError = err as {error?: {message?: string}; message?: string};
            return maybeError.error?.message ?? maybeError.message ?? 'Unknown';
        }
        return 'Unknown';
    };
}
